import { Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button } from "@mui/material";
import { useCreateFlavourMutation, useUpdateFlavourMutation, FlavourItem } from "../store/api/commonAPI";
import { showToast, showError } from "../constants/toasts";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editFlavor?: FlavourItem | null;
}

export default function AddFlavor({ open, onClose, setOpen, editFlavor }: ModalProps) {
  const [flavorName, setFlavorName] = useState("");
  const [color, setColor] = useState("#000000"); // default color
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<{ name?: string; color?: string }>({});

  const isEditMode = !!editFlavor;

  // Create and update flavour mutations
  const [createFlavour, { isLoading: isCreating }] = useCreateFlavourMutation();
  const [updateFlavour, { isLoading: isUpdating }] = useUpdateFlavourMutation();

  const isLoading = isCreating || isUpdating;

  // Reset form when modal opens/closes or when editFlavor changes
  useEffect(() => {
    if (!open) {
      setFlavorName("");
      setColor("#000000");
      setIsActive(true);
      setErrors({});
    } else if (editFlavor) {
      // Populate form with edit data
      setFlavorName(editFlavor.name || "");
      setColor(editFlavor.colourCode || editFlavor.color || "#000000");
      setIsActive(editFlavor.isActive ?? true);
      setErrors({});
    } else {
      // Reset for add mode
      setFlavorName("");
      setColor("#000000");
      setIsActive(true);
      setErrors({});
    }
  }, [open, editFlavor]);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setColor(newColor);
    // Clear color error when user changes it
    if (errors.color) {
      setErrors((prev) => ({ ...prev, color: undefined }));
    }
  };

  const handleColorInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setColor(newColor);
    // Clear color error when user changes it
    if (errors.color) {
      setErrors((prev) => ({ ...prev, color: undefined }));
    }
    // Real-time validation for better UX
    if (newColor && newColor.trim() !== "") {
      const trimmedColor = newColor.trim();
      // Check if it starts with #
      if (!trimmedColor.startsWith("#")) {
        setErrors((prev) => ({
          ...prev,
          color: "Color code must start with #",
        }));
      } else if (trimmedColor.length < 4) {
        // # + at least 3 characters for short hex
        setErrors((prev) => ({
          ...prev,
          color: "Color code is too short (minimum #000)",
        }));
      } else if (trimmedColor.length > 7) {
        setErrors((prev) => ({
          ...prev,
          color: "Color code is too long (maximum #000000)",
        }));
      } else if (!/^#[A-Fa-f0-9]{3,6}$/.test(trimmedColor)) {
        setErrors((prev) => ({
          ...prev,
          color: "Color code contains invalid characters (only 0-9, A-F allowed)",
        }));
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFlavorName(newName);
    // Clear name error when user types
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: { name?: string; color?: string } = {};

    // Validate name
    if (!flavorName.trim()) {
      newErrors.name = "Flavor name is required";
    } else if (flavorName.trim().length < 2) {
      newErrors.name = "Flavor name must be at least 2 characters";
    } else if (flavorName.trim().length > 40) {
      newErrors.name = "Flavor name must be 40 characters or less";
    }

    // Validate color (hex color code)
    const trimmedColor = color?.trim() || "";
    
    if (!trimmedColor) {
      newErrors.color = "Color code is required";
    } else if (!trimmedColor.startsWith("#")) {
      newErrors.color = "Color code must start with # (e.g., #FFFFFF)";
    } else if (trimmedColor.length < 4) {
      // # + at least 3 characters for short hex (#000)
      newErrors.color = "Color code is too short (minimum #000)";
    } else if (trimmedColor.length > 7) {
      newErrors.color = "Color code is too long (maximum #000000)";
    } else if (!/^#[A-Fa-f0-9]{3,6}$/.test(trimmedColor)) {
      // Check for valid hex characters (3 or 6 hex digits after #)
      if (/[^A-Fa-f0-9]/.test(trimmedColor.substring(1))) {
        newErrors.color = "Color code contains invalid characters (only 0-9, A-F allowed)";
      } else {
        newErrors.color = "Please enter a valid hex color code (e.g., #000000 or #FFF)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showError("Please fix the errors in the form");
      return;
    }

    try {
      if (isEditMode && editFlavor?._id) {
        // Update existing flavour
        const response = await updateFlavour({
          id: editFlavor._id,
          name: flavorName.trim(),
          isActive: isActive,
          colourCode: color,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage =
          response?.message || `Flavour "${flavorName.trim()}" updated successfully`;
        showToast(successMessage);
      } else {
        // Create new flavour
        const response = await createFlavour({
          name: flavorName.trim(),
          colourCode: color,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage =
          response?.message || `Flavour "${flavorName.trim()}" created successfully`;
        showToast(successMessage);
      }

      // Reset form and close modal
      setFlavorName("");
      setColor("#000000");
      setIsActive(true);
      setErrors({});
      setOpen(false);
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} flavour:`, error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        `Failed to ${isEditMode ? "update" : "create"} flavour. Please try again.`;

      showError(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setOpen(false);
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={handleClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{isEditMode ? "Edit Flavor" : "Add Flavor"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
              disabled={isLoading}
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={handleSubmit}>
            <div className="control_group">
              <label>Flavor Name *</label>
              <TextField
                placeholder="Enter Name"
                fullWidth
                hiddenLabel
                value={flavorName}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name || " "}
                disabled={isLoading}
                required
                inputProps={{ maxLength: 40 }}
                FormHelperTextProps={{
                  style: { margin: 0, minHeight: errors.name ? "auto" : "20px" }
                }}
              />
            </div>

            <div className="control_group">
              <label>Flavor Color Code *</label>
              <div className="flex_input">
                {/* Color Picker */}
                <TextField
                  type="color"
                  fullWidth
                  hiddenLabel
                  value={color}
                  onChange={handleColorChange}
                  disabled={isLoading}
                />

                {/* HEX Code Text Input */}
                <TextField
                  fullWidth
                  hiddenLabel
                  placeholder="#000000"
                  value={color}
                  onChange={handleColorInput}
                  error={!!errors.color}
                  helperText={errors.color}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isLoading}
              >
                {isLoading
                  ? isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
